/**
 * ImmobilienBereich — Inhalt der Immobilien-Sektionen im Finanzen-Cockpit.
 *
 * Sektionen: uebersicht | objekte | mietvertraege | mieteingaenge | kosten | afa |
 *   buchhaltung (Anlage-V + Veranlagung) | dokumente.
 * Objekte-Karten sind klickbar → Objekt-Detailseite (Bearbeiten + alle Daten).
 * Daten: immo_* Tabellen (Inhaber-only). Kein react-query (App-Standard).
 */
import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Building2, FileText, ArrowDownToLine, Receipt, TrendingDown, Calculator,
  Clock, Loader2, Plus, Check, AlertTriangle, Home, Users, Banknote,
  ExternalLink, Calendar, X, Euro, Pencil, ChevronRight,
} from 'lucide-react';
import { useImmoData, fmtEuro, fmtDatum, fmtProzent, type Row } from '@/hooks/useImmobilien';
import DokumentUpload, { oeffneDokument } from '@/components/immobilien/DokumentUpload';

const HEUTE = new Date().toISOString().slice(0, 10);
const AKT_JAHR = new Date().getFullYear();
const MONATE = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

const KAT_OPTIONS = [
  'instandhaltung','ausstattung','versicherung','grundsteuer','hausverwaltung',
  'abfall','wasser','strom','heizung','zinsen','renovierung','reinigung','sonstige',
];

// ============================================================
// Kleine UI-Bausteine (Tailwind, App-Stil)
// ============================================================
function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
    </div>
  );
}

function Leer({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
      <p className="text-sm text-gray-400">{text}</p>
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-xl font-bold mt-1" style={{ color: color || '#111827' }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: color + '1A', color }}>{text}</span>
  );
}

function H({ title, sub, action }: { title: string; sub?: string; action?: any }) {
  return (
    <div className="flex items-start justify-between mb-4 gap-3">
      <div>
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function einordnungBadge(e?: string) {
  if (e === 'v_und_v') return <Badge text="Vermietung (Anlage V)" color="#059669" />;
  if (e === 'betriebsvermoegen') return <Badge text="Betriebsimmobilie" color="#D97706" />;
  if (e === 'gemischt_vv_und_betrieb') return <Badge text="Mischnutzung" color="#D97706" />;
  if (e === 'privat') return <Badge text="Privat" color="#6B7280" />;
  return null;
}

// ============================================================
// HAUPT-KOMPONENTE
// ============================================================
export default function ImmobilienBereich({ section }: { section: string }) {
  const data = useImmoData();
  if (data.loading) return <Spinner />;

  switch (section) {
    case 'uebersicht':    return <Uebersicht d={data} />;
    case 'objekte':       return <Objekte d={data} />;
    case 'mietvertraege': return <Mietvertraege d={data} />;
    case 'mieteingaenge': return <Mieteingaenge d={data} />;
    case 'kosten':        return <Kosten d={data} />;
    case 'afa':           return <AfaSektion d={data} />;
    case 'buchhaltung':   return <AnlageV d={data} />;
    case 'dokumente':     return <Dokumente d={data} />;
    default:              return <Uebersicht d={data} />;
  }
}

// ------------------------------------------------------------
// ÜBERSICHT
// ------------------------------------------------------------
function Uebersicht({ d }: { d: ReturnType<typeof useImmoData> }) {
  const vv = d.objekte.filter(o => o.steuerliche_einordnung === 'v_und_v' && !o.verkauft);
  const aktiveVertraege = d.mietvertraege.filter(m => m.status === 'aktiv');
  const sollJahr = aktiveVertraege.reduce((s, m) => s + Number(m.warmmiete || m.kaltmiete || 0) * 12, 0);
  const kostenJahr = d.kosten.filter(k => Number(k.jahr) === AKT_JAHR).reduce((s, k) => s + Number(k.betrag || 0), 0);
  const wkJahr = d.kosten.filter(k => Number(k.jahr) === AKT_JAHR && k.steuerlich_absetzbar).reduce((s, k) => s + Number(k.betrag || 0), 0);
  const afaJahr = d.afa.reduce((s, a) => s + Number(a.afa_jahr_betrag || 0), 0);
  const offeneEing = d.mieteingaenge.filter(e => e.status !== 'bezahlt').length;
  const offeneFristen = d.fristen.filter(f => !f.erledigt).slice(0, 6);

  return (
    <div className="space-y-5">
      <H title="Immobilien — Übersicht" sub="Kennzahlen über alle Objekte" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Objekte (vermietet)" value={String(vv.length)} sub={`${d.objekte.length} gesamt`} />
        <Kpi label="Einheiten" value={String(d.einheiten.length || aktiveVertraege.length)} />
        <Kpi label="Soll-Miete / Jahr" value={fmtEuro(sollJahr)} color="#059669" sub="warm, aktive Verträge" />
        <Kpi label="Offene Mieteingänge" value={String(offeneEing)} color={offeneEing ? '#DC2626' : '#059669'} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={`Kosten ${AKT_JAHR}`} value={fmtEuro(kostenJahr)} color="#DC2626" />
        <Kpi label={`Werbungskosten ${AKT_JAHR}`} value={fmtEuro(wkJahr)} sub="steuerlich absetzbar" />
        <Kpi label="AfA / Jahr" value={fmtEuro(afaJahr)} sub="Gebäude-Abschreibung" />
        <Kpi label="Darlehen" value={String(d.darlehen.length)} sub="Finanzierungen" />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-bold text-gray-900">Nächste Fristen</h3>
        </div>
        {offeneFristen.length === 0 ? (
          <p className="text-sm text-gray-400">Keine offenen Fristen.</p>
        ) : (
          <div className="space-y-2">
            {offeneFristen.map(f => {
              const tage = Math.ceil((new Date(f.faellig_am).getTime() - Date.now()) / 86400000);
              const warn = tage <= (f.vorlauf_tage || 30);
              return (
                <div key={f.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{f.titel}</span>
                  <span className={warn ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                    {fmtDatum(f.faellig_am)} ({tage >= 0 ? `in ${tage} T` : `${-tage} T überf.`})
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Objekte</h3>
        <div className="space-y-2">
          {d.objekte.map(o => (
            <div key={o.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-gray-300" />
                <span className="text-gray-800 font-medium">{o.bezeichnung}</span>
                {o.verkauft && <Badge text="verkauft" color="#6B7280" />}
              </div>
              {einordnungBadge(o.steuerliche_einordnung)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// OBJEKTE (Karten klickbar → Detailseite)
// ------------------------------------------------------------
function Objekte({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState<Row | null>(null);
  const [form, setForm] = useState<any>({ bezeichnung: '', strasse: '', plz: '', ort: '', nutzungsart: 'vermietung', steuerliche_einordnung: 'v_und_v' });
  const darlehenFor = (id: string) => d.darlehen.filter(x => x.objekt_id === id);
  const einheitenFor = (id: string) => d.einheiten.filter(x => x.objekt_id === id);

  async function speichern() {
    if (!form.bezeichnung.trim()) { toast.error('Bezeichnung fehlt'); return; }
    const { error } = await supabase.from('immo_objekte').insert(form);
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success('Objekt angelegt');
    setShowForm(false);
    setForm({ bezeichnung: '', strasse: '', plz: '', ort: '', nutzungsart: 'vermietung', steuerliche_einordnung: 'v_und_v' });
    d.reload();
  }

  return (
    <div className="space-y-4">
      <H title="Objekte" sub="Karte anklicken → Detailseite (alle Daten + Bearbeiten)" action={
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1.5 text-sm font-semibold text-white px-3 py-2 rounded-lg" style={{ backgroundColor: '#B45309' }}>
          <Plus className="w-4 h-4" /> Objekt
        </button>
      } />

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Bezeichnung" value={form.bezeichnung} onChange={(v) => setForm({ ...form, bezeichnung: v })} />
            <Input label="Straße" value={form.strasse} onChange={(v) => setForm({ ...form, strasse: v })} />
            <Input label="PLZ" value={form.plz} onChange={(v) => setForm({ ...form, plz: v })} />
            <Input label="Ort" value={form.ort} onChange={(v) => setForm({ ...form, ort: v })} />
            <Select label="Nutzung" value={form.nutzungsart} options={['vermietung','eigennutzung','betrieb','gemischt']} onChange={(v) => setForm({ ...form, nutzungsart: v })} />
            <Select label="Steuerl. Einordnung" value={form.steuerliche_einordnung} options={['v_und_v','betriebsvermoegen','privat']} onChange={(v) => setForm({ ...form, steuerliche_einordnung: v })} />
          </div>
          <div className="flex gap-2">
            <button onClick={speichern} className="text-sm font-semibold text-white px-4 py-2 rounded-lg" style={{ backgroundColor: '#059669' }}>Speichern</button>
            <button onClick={() => setShowForm(false)} className="text-sm text-gray-500 px-4 py-2">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {d.objekte.map(o => (
          <div key={o.id} onClick={() => setDetail(o)}
            className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1">{o.bezeichnung}<ChevronRight className="w-4 h-4 text-gray-300" /></h3>
                <p className="text-xs text-gray-400">{[o.strasse, [o.plz, o.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {einordnungBadge(o.steuerliche_einordnung)}
                {o.verkauft && <Badge text="verkauft" color="#6B7280" />}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-3">
              <Detail k="Objektart" v={o.objektart || '—'} />
              <Detail k="Baujahr" v={o.baujahr || '—'} />
              <Detail k="Kaufdatum" v={fmtDatum(o.anschaffungsdatum)} />
              <Detail k="Kaufpreis" v={fmtEuro(o.anschaffungskosten)} />
              <Detail k="Wohnfläche" v={o.wohnflaeche_qm ? o.wohnflaeche_qm + ' m²' : '—'} />
              <Detail k="Verkehrswert" v={fmtEuro(o.verkehrswert)} />
              <Detail k="Eigentümer" v={o.eigentuemer || '—'} />
              <Detail k="Einheiten" v={String(einheitenFor(o.id).length || o.anzahl_einheiten || '—')} />
            </div>
            {darlehenFor(o.id).length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-50">
                <p className="text-[11px] font-semibold text-gray-500 mb-1">Finanzierung</p>
                {darlehenFor(o.id).map(dl => (
                  <div key={dl.id} className="flex items-center justify-between text-xs text-gray-600">
                    <span>{dl.bank}{dl.darlehensnummer ? ` · ${dl.darlehensnummer}` : ''}</span>
                    <span>{fmtEuro(dl.restschuld || dl.darlehensbetrag)}{dl.zinssatz ? ` · ${fmtProzent(dl.zinssatz)}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {detail && <ObjektDetail d={d} objekt={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ------------------------------------------------------------
// OBJEKT-DETAILSEITE (Modal: alle Daten + Bearbeiten)
// ------------------------------------------------------------
const EDIT_FELDER = ['bezeichnung','strasse','plz','ort','objektart','baujahr','anschaffungsdatum','anschaffungskosten','anschaffungsnebenkosten','wohnflaeche_qm','grundstuecksflaeche_qm','anzahl_einheiten','einheitswert','verkehrswert','verkehrswert_datum','eigentuemer','miteigentumsanteil_prozent','nutzungsart','steuerliche_einordnung','verkauft','verkaufsdatum','verkaufspreis','notiz'];

function ObjektDetail({ d, objekt, onClose }: { d: ReturnType<typeof useImmoData>; objekt: Row; onClose: () => void }) {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({ ...objekt });
  const darlehen = d.darlehen.filter(x => x.objekt_id === objekt.id);
  const vertraege = d.mietvertraege.filter(x => x.objekt_id === objekt.id);
  const einheiten = d.einheiten.filter(x => x.objekt_id === objekt.id);
  const doks = d.dokumente.filter(x => x.objekt_id === objekt.id);
  const afa = d.afa.filter(a => a.objekt_id === objekt.id);
  const mieterName = (id: string) => d.mieter.find(m => m.id === id)?.name || '—';
  const kostenJahre: Record<string, number> = {};
  d.kosten.filter(k => k.objekt_id === objekt.id).forEach(k => { kostenJahre[k.jahr] = (kostenJahre[k.jahr] || 0) + Number(k.betrag || 0); });
  const sollJahr = vertraege.filter(m => m.status === 'aktiv').reduce((s, m) => s + Number(m.warmmiete || m.kaltmiete || 0) * 12, 0);
  // Anlage-V Vorjahr
  const jv = AKT_JAHR - 1;
  const ein = d.mieteingaenge.filter(e => e.objekt_id === objekt.id && Number(e.jahr) === jv && e.status === 'bezahlt').reduce((s, e) => s + Number(e.ist_betrag || 0), 0);
  const wk = d.kosten.filter(k => k.objekt_id === objekt.id && Number(k.jahr) === jv && k.steuerlich_absetzbar).reduce((s, k) => s + Number(k.betrag || 0), 0);
  const afaSum = afa.reduce((s, a) => s + Number(a.afa_jahr_betrag || 0), 0);

  async function save() {
    setSaving(true);
    const upd: any = {};
    EDIT_FELDER.forEach(k => { upd[k] = form[k] === '' ? null : form[k]; });
    const { error } = await supabase.from('immo_objekte').update(upd).eq('id', objekt.id);
    setSaving(false);
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success('Gespeichert'); setEdit(false); d.reload();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-4" onClick={e => e.stopPropagation()}>
        {/* Kopf */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{objekt.bezeichnung}</h2>
            <p className="text-xs text-gray-400">{[objekt.strasse, [objekt.plz, objekt.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}</p>
            <div className="flex gap-1 mt-2">{einordnungBadge(objekt.steuerliche_einordnung)}{objekt.verkauft && <Badge text="verkauft" color="#6B7280" />}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEdit(v => !v)} className="flex items-center gap-1 text-xs font-semibold text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" />{edit ? 'Abbrechen' : 'Bearbeiten'}</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {edit ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Bezeichnung" value={form.bezeichnung || ''} onChange={v => setForm({ ...form, bezeichnung: v })} />
                <Input label="Straße" value={form.strasse || ''} onChange={v => setForm({ ...form, strasse: v })} />
                <Input label="PLZ" value={form.plz || ''} onChange={v => setForm({ ...form, plz: v })} />
                <Input label="Ort" value={form.ort || ''} onChange={v => setForm({ ...form, ort: v })} />
                <Input label="Objektart" value={form.objektart || ''} onChange={v => setForm({ ...form, objektart: v })} />
                <Input label="Baujahr" value={form.baujahr ?? ''} onChange={v => setForm({ ...form, baujahr: v })} />
                <Input label="Kaufdatum" type="date" value={form.anschaffungsdatum || ''} onChange={v => setForm({ ...form, anschaffungsdatum: v })} />
                <Input label="Kaufpreis (€)" value={form.anschaffungskosten ?? ''} onChange={v => setForm({ ...form, anschaffungskosten: v })} />
                <Input label="Kauf-Nebenkosten (€)" value={form.anschaffungsnebenkosten ?? ''} onChange={v => setForm({ ...form, anschaffungsnebenkosten: v })} />
                <Input label="Wohnfläche (m²)" value={form.wohnflaeche_qm ?? ''} onChange={v => setForm({ ...form, wohnflaeche_qm: v })} />
                <Input label="Grundstück (m²)" value={form.grundstuecksflaeche_qm ?? ''} onChange={v => setForm({ ...form, grundstuecksflaeche_qm: v })} />
                <Input label="Einheiten" value={form.anzahl_einheiten ?? ''} onChange={v => setForm({ ...form, anzahl_einheiten: v })} />
                <Input label="Einheitswert (€)" value={form.einheitswert ?? ''} onChange={v => setForm({ ...form, einheitswert: v })} />
                <Input label="Verkehrswert (€)" value={form.verkehrswert ?? ''} onChange={v => setForm({ ...form, verkehrswert: v })} />
                <Input label="Verkehrswert-Datum" type="date" value={form.verkehrswert_datum || ''} onChange={v => setForm({ ...form, verkehrswert_datum: v })} />
                <Input label="Eigentümer" value={form.eigentuemer || ''} onChange={v => setForm({ ...form, eigentuemer: v })} />
                <Input label="Miteigentum %" value={form.miteigentumsanteil_prozent ?? ''} onChange={v => setForm({ ...form, miteigentumsanteil_prozent: v })} />
                <Select label="Nutzung" value={form.nutzungsart || 'vermietung'} options={['vermietung','eigennutzung','betrieb','gemischt']} onChange={v => setForm({ ...form, nutzungsart: v })} />
                <Select label="Steuerl. Einordnung" value={form.steuerliche_einordnung || 'v_und_v'} options={['v_und_v','betriebsvermoegen','gemischt_vv_und_betrieb','privat']} onChange={v => setForm({ ...form, steuerliche_einordnung: v })} />
                <Input label="Verkaufsdatum" type="date" value={form.verkaufsdatum || ''} onChange={v => setForm({ ...form, verkaufsdatum: v })} />
                <Input label="Verkaufspreis (€)" value={form.verkaufspreis ?? ''} onChange={v => setForm({ ...form, verkaufspreis: v })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={!!form.verkauft} onChange={e => setForm({ ...form, verkauft: e.target.checked })} /> verkauft</label>
              <label className="block"><span className="text-xs text-gray-500">Notiz</span>
                <textarea value={form.notiz || ''} onChange={e => setForm({ ...form, notiz: e.target.value })} rows={4} className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400" /></label>
              <button onClick={save} disabled={saving} className="text-sm font-semibold text-white px-4 py-2 rounded-lg" style={{ backgroundColor: '#059669' }}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Soll-Miete / Jahr" value={fmtEuro(sollJahr)} color="#059669" />
                <Kpi label="AfA / Jahr" value={fmtEuro(afaSum)} />
                <Kpi label="Verkehrswert" value={fmtEuro(objekt.verkehrswert)} />
                <Kpi label="Darlehen-Rest" value={fmtEuro(darlehen.reduce((s, x) => s + Number(x.restschuld || 0), 0))} color="#DC2626" />
              </div>

              {/* Stammdaten */}
              <Block title="Stammdaten">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                  <Detail k="Objektart" v={objekt.objektart || '—'} />
                  <Detail k="Baujahr" v={objekt.baujahr || '—'} />
                  <Detail k="Kaufdatum" v={fmtDatum(objekt.anschaffungsdatum)} />
                  <Detail k="Kaufpreis" v={fmtEuro(objekt.anschaffungskosten)} />
                  <Detail k="Kauf-NK" v={fmtEuro(objekt.anschaffungsnebenkosten)} />
                  <Detail k="Wohnfläche" v={objekt.wohnflaeche_qm ? objekt.wohnflaeche_qm + ' m²' : '—'} />
                  <Detail k="Einheitswert" v={fmtEuro(objekt.einheitswert)} />
                  <Detail k="Eigentümer" v={objekt.eigentuemer || '—'} />
                  <Detail k="Miteigentum" v={objekt.miteigentumsanteil_prozent ? objekt.miteigentumsanteil_prozent + ' %' : '—'} />
                  {objekt.verkauft && <Detail k="Verkauf" v={fmtDatum(objekt.verkaufsdatum)} />}
                  {objekt.verkauft && <Detail k="Verkaufspreis" v={fmtEuro(objekt.verkaufspreis)} />}
                </div>
              </Block>

              {/* Finanzierung */}
              {darlehen.length > 0 && (
                <Block title={`Finanzierung (${darlehen.length})`}>
                  <div className="space-y-2">
                    {darlehen.map(dl => (
                      <div key={dl.id} className="text-xs border-b border-gray-50 pb-2 last:border-0">
                        <div className="flex items-center justify-between"><span className="font-medium text-gray-800">{dl.bank} {dl.darlehensnummer || ''}</span><span className="text-gray-700">{fmtEuro(dl.restschuld || dl.darlehensbetrag)}</span></div>
                        <div className="text-gray-400">{dl.typ || ''}{dl.zinssatz ? ` · ${fmtProzent(dl.zinssatz)}` : ''}{dl.monatliche_rate ? ` · ${fmtEuro(dl.monatliche_rate)}/Mt` : ''}{dl.zins_anteil_monat ? ` (Zins ${fmtEuro(dl.zins_anteil_monat)})` : ''}</div>
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {/* Mietverhältnisse */}
              {vertraege.length > 0 && (
                <Block title={`Mietverhältnisse (${vertraege.length})`}>
                  <div className="space-y-1.5">
                    {vertraege.map(m => (
                      <div key={m.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-800">{mieterName(m.mieter_id)}</span>
                        <span className="text-gray-500">{fmtEuro(m.kaltmiete)} kalt · <Badge text={m.status} color={m.status === 'aktiv' ? '#059669' : '#6B7280'} /></span>
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {/* Kosten je Jahr */}
              {Object.keys(kostenJahre).length > 0 && (
                <Block title="Kosten / Belege je Jahr">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(kostenJahre).sort((a, b) => Number(b[0]) - Number(a[0])).map(([j, s]) => (
                      <span key={j} className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1">{j}: <b>{fmtEuro(s)}</b></span>
                    ))}
                  </div>
                </Block>
              )}

              {/* AfA */}
              {afa.length > 0 && (
                <Block title="AfA">
                  {afa.map(a => <div key={a.id} className="flex items-center justify-between text-xs"><span className="text-gray-700">{a.bezeichnung}</span><span className="font-medium" style={{ color: '#059669' }}>{fmtEuro(a.afa_jahr_betrag)}/J</span></div>)}
                </Block>
              )}

              {/* Anlage V Vorjahr */}
              {objekt.steuerliche_einordnung === 'v_und_v' && (
                <Block title={`Anlage V — ${jv} (Hochrechnung)`}>
                  <div className="space-y-1 text-sm">
                    <Zeile k="Mieteinnahmen (Ist)" v={fmtEuro(ein)} color="#059669" />
                    <Zeile k="− Werbungskosten" v={fmtEuro(wk)} color="#DC2626" />
                    <Zeile k="− AfA" v={fmtEuro(afaSum)} color="#DC2626" />
                    <div className="border-t border-gray-100 pt-1 mt-1"><Zeile k="= Überschuss/Verlust" v={fmtEuro(ein - wk - afaSum)} bold color={(ein - wk - afaSum) >= 0 ? '#111827' : '#DC2626'} /></div>
                  </div>
                </Block>
              )}

              {/* Dokumente */}
              {doks.length > 0 && (
                <Block title={`Dokumente (${doks.length})`}>
                  <div className="space-y-1">
                    {doks.map(dok => (
                      <div key={dok.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">{dok.titel} <span className="text-gray-400">· {dok.kategorie}</span></span>
                        {dok.drive_url && <a href={dok.drive_url} target="_blank" rel="noreferrer" className="text-amber-700"><ExternalLink className="w-3.5 h-3.5" /></a>}
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {objekt.notiz && (
                <Block title="Notiz / Hinweise">
                  <p className="text-[11px] text-gray-500 leading-relaxed whitespace-pre-wrap">{objekt.notiz}</p>
                </Block>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ title, children }: { title: string; children: any }) {
  return (
    <div className="bg-gray-50/60 border border-gray-100 rounded-xl p-4">
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
      {children}
    </div>
  );
}

function Detail({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-400">{k}</span>
      <span className="text-gray-800 font-medium text-right">{v}</span>
    </div>
  );
}


// ============================================================
// MIETEN-BOARD (§21(2) verbilligt-Ampel + Mietvorschläge)
// ------------------------------------------------------------
// Ortsübliche Vergleichsmiete (€/m²/Monat, Wohnen). GROBER SCHÄTZWERT –
// bitte je Wohnung anhand des aktuellen Mietspiegels Lüdenscheid bestätigen.
// Optional je Objekt-Bezeichnung überschreibbar (Key = objekt.bezeichnung).
const ORTSUEBLICH_QM = 7.0; // €/m²/Monat (Wohnen Lüdenscheid, Schätzwert – per Mietspiegel verfeinern)
const ORTSUEBLICH_MAP: Record<string, number> = {
  // 'Rahmedestraße 65': 7.2,  // Beispiel: je Objekt überschreiben
};
function ortsueblichQm(objektName?: string): number {
  if (objektName && ORTSUEBLICH_MAP[objektName] != null) return ORTSUEBLICH_MAP[objektName];
  return ORTSUEBLICH_QM;
}

type AmpelKey = 'rot' | 'gelb' | 'gruen' | 'na';
function ampelInfo(ratio: number | null): { key: AmpelKey; color: string; label: string } {
  if (ratio == null || !isFinite(ratio)) return { key: 'na', color: '#9CA3AF', label: 'n/a' };
  if (ratio < 0.66) return { key: 'rot', color: '#DC2626', label: 'rot' };
  if (ratio < 0.90) return { key: 'gelb', color: '#D97706', label: 'gelb' };
  return { key: 'gruen', color: '#059669', label: 'grün' };
}

function AmpelDot({ color }: { color: string }) {
  return <span className="inline-block w-2.5 h-2.5 rounded-full align-middle" style={{ backgroundColor: color }} />;
}

type SortKey = 'mieter' | 'ratio' | 'spielraum';

function MietenBoard({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [sortKey, setSortKey] = useState<SortKey>('ratio');

  const mieterName = (id: string) => d.mieter.find(m => m.id === id)?.name || '—';
  const einheitFor = (id?: string) => (id ? d.einheiten.find(e => e.id === id) : undefined);

  const rows = useMemo(() => {
    const aktive = d.mietvertraege.filter(m => m.status === 'aktiv');
    const list = aktive.map(m => {
      const einheit = einheitFor(m.einheit_id);
      const obj = d.objekte.find(o => o.id === m.objekt_id);
      const objektName = obj?.bezeichnung || '—';
      const qmRaw = einheit?.wohnflaeche_qm;
      const qm = qmRaw != null && Number(qmRaw) > 0 ? Number(qmRaw) : null;
      const kalt = m.kaltmiete != null ? Number(m.kaltmiete) : null;
      const einheitTyp = String(einheit?.typ || '').toLowerCase();
      const mname = mieterName(m.mieter_id);
      // Gewerbe (z. B. Sparkasse) → keine Wohnraum-Ampel nach §21(2) EStG.
      const istGewerbe = einheitTyp.includes('gewerbe') || /sparkasse/i.test(mname);

      const istQm = qm && kalt != null ? kalt / qm : null;            // Ist €/m²
      const ortsQm = ortsueblichQm(objektName);                       // Vergleich €/m²
      const ratio = istQm != null ? istQm / ortsQm : null;            // Ist ÷ ortsüblich
      const sollKalt = qm ? ortsQm * qm : null;                       // Soll-Kaltmiete (ortsüblich)

      // Kappungsgrenze §558 BGB: max +20 % in 3 Jahren (Wohnraum).
      let spielraum: number | null = null;
      let zielMiete: number | null = null;
      if (!istGewerbe && kalt != null && sollKalt != null && ratio != null && ratio < 0.90) {
        zielMiete = Math.min(sollKalt, kalt * 1.20);
        spielraum = Math.max(0, zielMiete - kalt);
      }

      return {
        id: m.id, mieter: mname, objektName,
        einheitLabel: einheit ? (einheit.bezeichnung || einheit.etage || einheit.typ || '—') : '—',
        qm, kalt, istQm, ortsQm, ratio, sollKalt, istGewerbe, spielraum, zielMiete,
      };
    });

    const dir = (a: typeof list[number], b: typeof list[number]) => {
      if (sortKey === 'mieter') return a.mieter.localeCompare(b.mieter);
      if (sortKey === 'spielraum') return (b.spielraum ?? -1) - (a.spielraum ?? -1);
      // ratio: niedrigste zuerst (höchstes Erhöhungspotenzial / Risiko oben)
      return (a.ratio ?? 99) - (b.ratio ?? 99);
    };
    return [...list].sort(dir);
  }, [d.mietvertraege, d.einheiten, d.objekte, d.mieter, sortKey]);

  if (rows.length === 0) return null;

  const summeSpielraum = rows.reduce((s, r) => s + (r.spielraum || 0), 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-gray-300" />Mieten-Board</h3>
          <p className="text-xs text-gray-400 mt-0.5">Aktive Verhältnisse · §21(2)-Ampel & Mietvorschläge · mögl. Spielraum gesamt {fmtEuro(summeSpielraum)}/Monat</p>
        </div>
        <div className="flex gap-1 text-[11px]">
          <button onClick={() => setSortKey('ratio')} className="px-2 py-1 rounded-lg border" style={sortKey === 'ratio' ? { backgroundColor: '#B45309', color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }}>Verbilligung</button>
          <button onClick={() => setSortKey('spielraum')} className="px-2 py-1 rounded-lg border" style={sortKey === 'spielraum' ? { backgroundColor: '#B45309', color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }}>Spielraum</button>
          <button onClick={() => setSortKey('mieter')} className="px-2 py-1 rounded-lg border" style={sortKey === 'mieter' ? { backgroundColor: '#B45309', color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }}>Mieter</button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              <th className="px-2 py-2 text-left font-medium">Mieter · Objekt · Einheit</th>
              <th className="px-2 py-2 text-right font-medium">Fläche</th>
              <th className="px-2 py-2 text-right font-medium">Ist-Kalt</th>
              <th className="px-2 py-2 text-right font-medium">€/m²</th>
              <th className="px-2 py-2 text-right font-medium">Ortsübl. €/m²</th>
              <th className="px-2 py-2 text-right font-medium">Soll-Kalt</th>
              <th className="px-2 py-2 text-center font-medium">Ampel</th>
              <th className="px-2 py-2 text-left font-medium">Vorschlag</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const amp = r.istGewerbe
                ? { key: 'na' as AmpelKey, color: '#6B7280', label: 'Gewerbe' }
                : ampelInfo(r.ratio);
              return (
                <tr key={r.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-2 py-2">
                    <div className="font-semibold text-gray-900">{r.mieter}</div>
                    <div className="text-gray-400">{r.objektName} · {r.einheitLabel}</div>
                  </td>
                  <td className="px-2 py-2 text-right text-gray-700">{r.qm != null ? `${r.qm} m²` : <span className="text-amber-600">qm fehlt</span>}</td>
                  <td className="px-2 py-2 text-right text-gray-800 font-medium">{r.kalt != null ? fmtEuro(r.kalt) : '–'}</td>
                  <td className="px-2 py-2 text-right text-gray-700">{r.istQm != null ? fmtEuro(r.istQm) : '–'}</td>
                  <td className="px-2 py-2 text-right text-gray-400">{r.istGewerbe ? '–' : fmtEuro(r.ortsQm)}</td>
                  <td className="px-2 py-2 text-right text-gray-700">{!r.istGewerbe && r.sollKalt != null ? fmtEuro(r.sollKalt) : '–'}</td>
                  <td className="px-2 py-2 text-center" title={amp.key === 'na' && !r.istGewerbe ? 'qm ergänzen' : ''}>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: amp.color + '1A', color: amp.color }}>
                      <AmpelDot color={amp.color} />{amp.label}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-left">
                    {r.istGewerbe ? (
                      <span className="text-gray-400">Gewerbe – §21(2) n. a.</span>
                    ) : r.spielraum != null && r.spielraum > 0 && r.zielMiete != null ? (
                      <span className="text-gray-700">
                        +{fmtEuro(r.spielraum)} → Ziel {fmtEuro(r.zielMiete)}
                        <span className="block text-[10px] text-gray-400">Vorschlag (rechtlich prüfen) · Kappung +20 %/3J §558 BGB</span>
                      </span>
                    ) : r.ratio == null ? (
                      <span className="text-gray-400">qm ergänzen</span>
                    ) : (
                      <span className="text-gray-400">ortsüblich – kein Bedarf</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5"><AmpelDot color="#DC2626" /> &lt; 66 % – Werbungskosten-Kürzung droht (§21(2) EStG)</span>
        <span className="inline-flex items-center gap-1.5"><AmpelDot color="#D97706" /> 66–90 % – Erhöhung prüfen</span>
        <span className="inline-flex items-center gap-1.5"><AmpelDot color="#059669" /> ≥ 90 % – unkritisch</span>
        <span className="inline-flex items-center gap-1.5"><AmpelDot color="#9CA3AF" /> n/a – qm fehlt</span>
      </div>
      <p className="mt-1 text-[10px] text-gray-400 leading-relaxed">
        Keine Rechtsberatung; ortsübliche Werte und qm bitte je Wohnung bestätigen.
      </p>
    </div>
  );
}

// ------------------------------------------------------------
// MIETVERTRÄGE (+ Nebenkosten-Subtab)
// ------------------------------------------------------------
function Mietvertraege({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [tab, setTab] = useState<'vertraege' | 'nebenkosten'>('vertraege');
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '—';
  const mieterName = (id: string) => d.mieter.find(m => m.id === id)?.name || '—';

  return (
    <div className="space-y-4">
      <H title="Mietverhältnisse" sub="Verträge & Nebenkostenabrechnungen" />
      {tab === 'vertraege' && <MietenBoard d={d} />}
      <div className="flex gap-1">
        <TabBtn active={tab === 'vertraege'} onClick={() => setTab('vertraege')}>Verträge</TabBtn>
        <TabBtn active={tab === 'nebenkosten'} onClick={() => setTab('nebenkosten')}>Nebenkostenabrechnung</TabBtn>
      </div>

      {tab === 'vertraege' ? (
        d.mietvertraege.length === 0 ? <Leer icon={FileText} text="Keine Mietverträge erfasst" /> : (
          <div className="space-y-3">
            {d.mietvertraege.map(m => (
              <div key={m.id} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Users className="w-4 h-4 text-gray-300" />{mieterName(m.mieter_id)}</h3>
                    <p className="text-xs text-gray-400">{objName(m.objekt_id)}</p>
                  </div>
                  <Badge text={m.status} color={m.status === 'aktiv' ? '#059669' : '#6B7280'} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs mt-2">
                  <Detail k="Kaltmiete" v={fmtEuro(m.kaltmiete)} />
                  <Detail k="NK-Vorausz." v={fmtEuro(m.nebenkosten_vorauszahlung)} />
                  <Detail k="Warmmiete" v={fmtEuro(m.warmmiete)} />
                  <Detail k="Kaution" v={fmtEuro(m.kaution)} />
                  <Detail k="Beginn" v={fmtDatum(m.mietbeginn)} />
                  <Detail k="Ende" v={m.mietende ? fmtDatum(m.mietende) : 'unbefristet'} />
                  <Detail k="Zahltag" v={m.zahltag || '—'} />
                  <Detail k="Index" v={m.indexmiete ? 'ja' : 'nein'} />
                </div>
                {m.notiz && <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">{m.notiz}</p>}
              </div>
            ))}
          </div>
        )
      ) : (
        <Nebenkosten d={d} />
      )}
    </div>
  );
}

function Nebenkosten({ d }: { d: ReturnType<typeof useImmoData> }) {
  const vvObjekte = d.objekte.filter(o => o.steuerliche_einordnung === 'v_und_v');
  const [form, setForm] = useState<any>({
    objekt_id: vvObjekte[0]?.id || '', abrechnungsjahr: AKT_JAHR - 1,
    gesamt_umlagefaehige_kosten: '', vorauszahlungen: '', umlageschluessel: 'wohnflaeche',
  });
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '—';
  const nachzahlung = (Number(form.gesamt_umlagefaehige_kosten) || 0) - (Number(form.vorauszahlungen) || 0);

  async function anlegen() {
    if (!form.objekt_id) { toast.error('Objekt wählen'); return; }
    const row = {
      objekt_id: form.objekt_id,
      abrechnungsjahr: Number(form.abrechnungsjahr),
      gesamt_umlagefaehige_kosten: Number(form.gesamt_umlagefaehige_kosten) || 0,
      vorauszahlungen: Number(form.vorauszahlungen) || 0,
      mieter_anteil: Number(form.gesamt_umlagefaehige_kosten) || 0,
      nachzahlung_guthaben: nachzahlung,
      umlageschluessel: form.umlageschluessel,
      erstellt_am: HEUTE,
      status: 'entwurf',
    };
    const { error } = await supabase.from('immo_nebenkostenabrechnung').insert(row);
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success('Abrechnung angelegt');
    setForm({ ...form, gesamt_umlagefaehige_kosten: '', vorauszahlungen: '' });
    d.reload();
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-sm font-bold text-gray-900">Neue Nebenkostenabrechnung</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select label="Objekt" value={form.objekt_id} options={vvObjekte.map(o => o.id)} labels={vvObjekte.map(o => o.bezeichnung)} onChange={(v) => setForm({ ...form, objekt_id: v })} />
          <Input label="Abrechnungsjahr" value={String(form.abrechnungsjahr)} onChange={(v) => setForm({ ...form, abrechnungsjahr: v })} />
          <Select label="Umlageschlüssel" value={form.umlageschluessel} options={['wohnflaeche','personen','verbrauch','einheiten']} onChange={(v) => setForm({ ...form, umlageschluessel: v })} />
          <Input label="Umlagefähige Kosten (€)" value={form.gesamt_umlagefaehige_kosten} onChange={(v) => setForm({ ...form, gesamt_umlagefaehige_kosten: v })} />
          <Input label="Vorauszahlungen (€)" value={form.vorauszahlungen} onChange={(v) => setForm({ ...form, vorauszahlungen: v })} />
          <div className="flex flex-col justify-end">
            <span className="text-xs text-gray-400">{nachzahlung >= 0 ? 'Nachzahlung Mieter' : 'Guthaben Mieter'}</span>
            <span className="text-lg font-bold" style={{ color: nachzahlung >= 0 ? '#DC2626' : '#059669' }}>{fmtEuro(Math.abs(nachzahlung))}</span>
          </div>
        </div>
        <button onClick={anlegen} className="text-sm font-semibold text-white px-4 py-2 rounded-lg" style={{ backgroundColor: '#059669' }}>Abrechnung anlegen</button>
      </div>

      {d.nka.length === 0 ? <Leer icon={Calculator} text="Noch keine Nebenkostenabrechnungen" /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-gray-500 text-xs">
              <th className="text-left font-medium px-3 py-2">Objekt</th>
              <th className="text-left font-medium px-3 py-2">Jahr</th>
              <th className="text-right font-medium px-3 py-2">Umlagefähig</th>
              <th className="text-right font-medium px-3 py-2">Vorausz.</th>
              <th className="text-right font-medium px-3 py-2">Saldo</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
            </tr></thead>
            <tbody>
              {d.nka.map(n => (
                <tr key={n.id} className="border-t border-gray-50">
                  <td className="px-3 py-2 text-gray-800">{objName(n.objekt_id)}</td>
                  <td className="px-3 py-2 text-gray-600">{n.abrechnungsjahr}</td>
                  <td className="px-3 py-2 text-right">{fmtEuro(n.gesamt_umlagefaehige_kosten)}</td>
                  <td className="px-3 py-2 text-right">{fmtEuro(n.vorauszahlungen)}</td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: Number(n.nachzahlung_guthaben) >= 0 ? '#DC2626' : '#059669' }}>{fmtEuro(n.nachzahlung_guthaben)}</td>
                  <td className="px-3 py-2"><Badge text={n.status} color={n.status === 'bezahlt' ? '#059669' : '#6B7280'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400">Hinweis: Die 12-Monats-Frist für die Abrechnung gegenüber dem Mieter (§ 556 Abs. 3 BGB) wird im Fristen-/Übersichtsbereich überwacht.</p>
    </div>
  );
}

// ------------------------------------------------------------
// MIETEINGÄNGE (Soll/Ist je Monat)
// ------------------------------------------------------------
function Mieteingaenge({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [jahr, setJahr] = useState(AKT_JAHR);
  const aktive = d.mietvertraege.filter(m => m.status === 'aktiv');
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '—';
  const mieterName = (id: string) => d.mieter.find(m => m.id === id)?.name || '—';
  const eingFor = (mvId: string, monat: number) =>
    d.mieteingaenge.find(e => e.mietvertrag_id === mvId && Number(e.jahr) === jahr && Number(e.monat) === monat);

  async function sollAnlegen(mv: Row) {
    const betrag = Number(mv.warmmiete || mv.kaltmiete || 0);
    const rows = Array.from({ length: 12 }, (_, i) => ({
      mietvertrag_id: mv.id, objekt_id: mv.objekt_id, jahr, monat: i + 1,
      soll_betrag: betrag, status: 'offen',
    }));
    const { error } = await supabase.from('immo_mieteingaenge').upsert(rows, { onConflict: 'mietvertrag_id,jahr,monat', ignoreDuplicates: true });
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success(`Soll ${jahr} angelegt`);
    d.reload();
  }

  async function togglePaid(mv: Row, monat: number, existing?: Row) {
    if (existing) {
      const bezahlt = existing.status === 'bezahlt';
      const { error } = await supabase.from('immo_mieteingaenge').update({
        status: bezahlt ? 'offen' : 'bezahlt',
        ist_betrag: bezahlt ? 0 : Number(existing.soll_betrag || 0),
        eingegangen_am: bezahlt ? null : HEUTE,
      }).eq('id', existing.id);
      if (error) { toast.error('Fehler: ' + error.message); return; }
    } else {
      const betrag = Number(mv.warmmiete || mv.kaltmiete || 0);
      const { error } = await supabase.from('immo_mieteingaenge').insert({
        mietvertrag_id: mv.id, objekt_id: mv.objekt_id, jahr, monat,
        soll_betrag: betrag, ist_betrag: betrag, status: 'bezahlt', eingegangen_am: HEUTE,
      });
      if (error) { toast.error('Fehler: ' + error.message); return; }
    }
    d.reload();
  }

  const jahre = [AKT_JAHR + 1, AKT_JAHR, AKT_JAHR - 1, AKT_JAHR - 2];

  return (
    <div className="space-y-4">
      <H title="Mieteingänge" sub="Soll/Ist-Abgleich je Monat — Monat anklicken = bezahlt" action={
        <select value={jahr} onChange={e => setJahr(Number(e.target.value))} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          {jahre.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
      } />
      {aktive.length === 0 ? <Leer icon={ArrowDownToLine} text="Keine aktiven Mietverträge" /> : aktive.map(mv => {
        const soll = Number(mv.warmmiete || mv.kaltmiete || 0);
        let istSum = 0, sollSum = 0;
        for (let mo = 1; mo <= 12; mo++) { const e = eingFor(mv.id, mo); sollSum += soll; if (e && e.status === 'bezahlt') istSum += Number(e.ist_betrag || soll); }
        return (
          <div key={mv.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900">{mieterName(mv.mieter_id)}</h3>
                <p className="text-xs text-gray-400">{objName(mv.objekt_id)} · Soll {fmtEuro(soll)}/Monat</p>
              </div>
              <button onClick={() => sollAnlegen(mv)} className="text-xs font-semibold text-amber-700 border border-amber-200 px-2.5 py-1.5 rounded-lg hover:bg-amber-50">Soll {jahr} anlegen</button>
            </div>
            <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
              {MONATE.map((mn, i) => {
                const e = eingFor(mv.id, i + 1);
                const bezahlt = e && e.status === 'bezahlt';
                return (
                  <button key={i} onClick={() => togglePaid(mv, i + 1, e)} title={mn + ' ' + jahr}
                    className="flex flex-col items-center justify-center py-2 rounded-lg border text-[11px] font-medium transition-colors"
                    style={{ backgroundColor: bezahlt ? '#05966915' : '#fff', borderColor: bezahlt ? '#059669' : '#E5E7EB', color: bezahlt ? '#059669' : '#9CA3AF' }}>
                    {bezahlt ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5" />}
                    {mn}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 text-xs">
              <span className="text-gray-400">Ist {fmtEuro(istSum)} / Soll {fmtEuro(sollSum)}</span>
              <span className="font-semibold" style={{ color: istSum >= sollSum ? '#059669' : '#DC2626' }}>offen {fmtEuro(Math.max(0, sollSum - istSum))}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------
// KOSTEN / BELEGE
// ------------------------------------------------------------
function Kosten({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [objektFilter, setObjektFilter] = useState('alle');
  const [jahrFilter, setJahrFilter] = useState(AKT_JAHR);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({
    objekt_id: d.objekte[0]?.id || '', datum: HEUTE, kategorie: 'instandhaltung',
    beschreibung: '', betrag: '', lieferant: '', umlagefaehig: false, steuerlich_absetzbar: true,
  });
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '—';
  const jahre = useMemo(() => Array.from(new Set(d.kosten.map(k => Number(k.jahr)))).sort((a, b) => b - a), [d.kosten]);
  if (!jahre.includes(AKT_JAHR)) jahre.unshift(AKT_JAHR);

  const gefiltert = d.kosten.filter(k =>
    (objektFilter === 'alle' || k.objekt_id === objektFilter) && Number(k.jahr) === jahrFilter);
  const summe = gefiltert.reduce((s, k) => s + Number(k.betrag || 0), 0);
  const wkSumme = gefiltert.filter(k => k.steuerlich_absetzbar).reduce((s, k) => s + Number(k.betrag || 0), 0);
  const perKat: Record<string, number> = {};
  gefiltert.forEach(k => { perKat[k.kategorie || 'sonstige'] = (perKat[k.kategorie || 'sonstige'] || 0) + Number(k.betrag || 0); });

  async function speichern() {
    if (!form.objekt_id || !form.betrag) { toast.error('Objekt und Betrag erforderlich'); return; }
    const { error } = await supabase.from('immo_kosten').insert({ ...form, betrag: Number(form.betrag) });
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success('Beleg erfasst');
    setShowForm(false);
    setForm({ ...form, beschreibung: '', betrag: '', lieferant: '' });
    d.reload();
  }

  return (
    <div className="space-y-4">
      <H title="Kosten / Belege" sub="Werbungskosten & Betriebskosten je Objekt" action={
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1.5 text-sm font-semibold text-white px-3 py-2 rounded-lg" style={{ backgroundColor: '#B45309' }}><Plus className="w-4 h-4" /> Beleg</button>
      } />

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label="Objekt" value={form.objekt_id} options={d.objekte.map(o => o.id)} labels={d.objekte.map(o => o.bezeichnung)} onChange={(v) => setForm({ ...form, objekt_id: v })} />
            <Input label="Datum" type="date" value={form.datum} onChange={(v) => setForm({ ...form, datum: v })} />
            <Select label="Kategorie" value={form.kategorie} options={KAT_OPTIONS} onChange={(v) => setForm({ ...form, kategorie: v })} />
            <Input label="Beschreibung" value={form.beschreibung} onChange={(v) => setForm({ ...form, beschreibung: v })} />
            <Input label="Betrag (€)" value={form.betrag} onChange={(v) => setForm({ ...form, betrag: v })} />
            <Input label="Lieferant" value={form.lieferant} onChange={(v) => setForm({ ...form, lieferant: v })} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={form.steuerlich_absetzbar} onChange={e => setForm({ ...form, steuerlich_absetzbar: e.target.checked })} /> steuerlich absetzbar</label>
            <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={form.umlagefaehig} onChange={e => setForm({ ...form, umlagefaehig: e.target.checked })} /> umlagefähig (NK)</label>
          </div>
          <button onClick={speichern} className="text-sm font-semibold text-white px-4 py-2 rounded-lg" style={{ backgroundColor: '#059669' }}>Speichern</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <select value={objektFilter} onChange={e => setObjektFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          <option value="alle">Alle Objekte</option>
          {d.objekte.map(o => <option key={o.id} value={o.id}>{o.bezeichnung}</option>)}
        </select>
        <select value={jahrFilter} onChange={e => setJahrFilter(Number(e.target.value))} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          {jahre.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={`Summe ${jahrFilter}`} value={fmtEuro(summe)} color="#DC2626" />
        <Kpi label="davon Werbungskosten" value={fmtEuro(wkSumme)} />
        <Kpi label="Belege" value={String(gefiltert.length)} />
        <Kpi label="Kategorien" value={String(Object.keys(perKat).length)} />
      </div>

      {gefiltert.length === 0 ? <Leer icon={Receipt} text="Keine Belege für diese Auswahl" /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-gray-500 text-xs">
              <th className="text-left font-medium px-3 py-2">Datum</th>
              <th className="text-left font-medium px-3 py-2">Objekt</th>
              <th className="text-left font-medium px-3 py-2">Kategorie</th>
              <th className="text-left font-medium px-3 py-2">Beschreibung</th>
              <th className="text-right font-medium px-3 py-2">Betrag</th>
              <th className="text-center font-medium px-3 py-2">WK</th>
            </tr></thead>
            <tbody>
              {gefiltert.map(k => (
                <tr key={k.id} className="border-t border-gray-50">
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDatum(k.datum)}</td>
                  <td className="px-3 py-2 text-gray-600">{objName(k.objekt_id)}</td>
                  <td className="px-3 py-2"><Badge text={k.kategorie || 'sonstige'} color="#6B7280" /></td>
                  <td className="px-3 py-2 text-gray-800">{k.beschreibung}{k.lieferant ? ` · ${k.lieferant}` : ''}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">{fmtEuro(k.betrag)}</td>
                  <td className="px-3 py-2 text-center">{k.steuerlich_absetzbar ? <Check className="w-4 h-4 text-emerald-600 inline" /> : <X className="w-4 h-4 text-gray-300 inline" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// AfA / ABSCHREIBUNGEN
// ------------------------------------------------------------
function AfaSektion({ d }: { d: ReturnType<typeof useImmoData> }) {
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '—';
  const gesamt = d.afa.reduce((s, a) => s + Number(a.afa_jahr_betrag || 0), 0);
  return (
    <div className="space-y-4">
      <H title="AfA / Abschreibungen" sub="Gebäude-AfA und Sonderabschreibungen für die Steuererklärung" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Kpi label="AfA gesamt / Jahr" value={fmtEuro(gesamt)} color="#059669" />
        <Kpi label="AfA-Positionen" value={String(d.afa.length)} />
      </div>
      {d.afa.length === 0 ? <Leer icon={TrendingDown} text="Keine AfA hinterlegt" /> : d.afa.map(a => (
        <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900">{a.bezeichnung}</h3>
              <p className="text-xs text-gray-400">{objName(a.objekt_id)}{a.paragraph ? ` · ${a.paragraph}` : ''}</p>
            </div>
            <span className="text-base font-bold" style={{ color: '#059669' }}>{fmtEuro(a.afa_jahr_betrag)}<span className="text-xs text-gray-400 font-normal">/Jahr</span></span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs mt-3">
            <Detail k="AfA-Basis" v={fmtEuro(a.afa_basis)} />
            <Detail k="AfA-Satz" v={fmtProzent(a.afa_satz)} />
            <Detail k="Methode" v={a.afa_methode || '—'} />
            <Detail k="Beginn" v={fmtDatum(a.beginn_datum)} />
          </div>
          {a.notiz && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-3 leading-relaxed">{a.notiz}</p>}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------
// ANLAGE V + VERANLAGUNG (Sektion 'buchhaltung')
// ------------------------------------------------------------
function AnlageV({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [jahr, setJahr] = useState(AKT_JAHR - 1);
  const vv = d.objekte.filter(o => o.steuerliche_einordnung === 'v_und_v');
  const jahre = [AKT_JAHR, AKT_JAHR - 1, AKT_JAHR - 2, AKT_JAHR - 3];

  function fuerObjekt(objId: string) {
    const einnahmen = d.mieteingaenge
      .filter(e => e.objekt_id === objId && Number(e.jahr) === jahr && e.status === 'bezahlt')
      .reduce((s, e) => s + Number(e.ist_betrag || 0), 0);
    const wk = d.kosten
      .filter(k => k.objekt_id === objId && Number(k.jahr) === jahr && k.steuerlich_absetzbar)
      .reduce((s, k) => s + Number(k.betrag || 0), 0);
    const afa = d.afa.filter(a => a.objekt_id === objId).reduce((s, a) => s + Number(a.afa_jahr_betrag || 0), 0);
    return { einnahmen, wk, afa, ueberschuss: einnahmen - wk - afa };
  }

  const statusColor = (s?: string) => s === 'schaetzung' ? '#DC2626' : s === 'eingereicht' ? '#059669' : '#D97706';

  return (
    <div className="space-y-5">
      <H title="Buchhaltung / Steuer" sub="Veranlagung je Jahr + Anlage-V-Hochrechnung" />

      {d.steuerjahre.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <div className="px-4 pt-3 pb-1 text-sm font-bold text-gray-900">Veranlagung / Einkommensteuer</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-gray-500 text-xs">
              <th className="text-left font-medium px-3 py-2">Jahr</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
              <th className="text-right font-medium px-3 py-2">festges. ESt</th>
              <th className="text-right font-medium px-3 py-2">Erstattung / Nachz.</th>
              <th className="text-right font-medium px-3 py-2">Potenzial</th>
              <th className="text-left font-medium px-3 py-2">Hinweis</th>
            </tr></thead>
            <tbody>
              {d.steuerjahre.map(s => (
                <tr key={s.id} className="border-t border-gray-50 align-top">
                  <td className="px-3 py-2 font-semibold text-gray-800">{s.jahr}</td>
                  <td className="px-3 py-2"><Badge text={s.status} color={statusColor(s.status)} /></td>
                  <td className="px-3 py-2 text-right text-gray-700">{fmtEuro(s.festgesetzte_est)}</td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: Number(s.erstattung_nachzahlung) >= 0 ? '#059669' : '#DC2626' }}>{s.erstattung_nachzahlung != null ? fmtEuro(s.erstattung_nachzahlung) : '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{s.optimierung_min ? `${fmtEuro(s.optimierung_min)}–${fmtEuro(s.optimierung_max)}` : '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">{s.notiz}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <H title="Anlage V — Hochrechnung" sub="Ergebnis je Objekt (Vermietung & Verpachtung)" action={
        <select value={jahr} onChange={e => setJahr(Number(e.target.value))} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          {jahre.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
      } />
      {vv.length === 0 ? <Leer icon={Calculator} text="Keine Vermietungsobjekte" /> : vv.map(o => {
        const r = fuerObjekt(o.id);
        return (
          <div key={o.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">{o.bezeichnung}</h3>
            <div className="space-y-1.5 text-sm">
              <Zeile k="Mieteinnahmen (Ist)" v={fmtEuro(r.einnahmen)} color="#059669" />
              <Zeile k="− Werbungskosten" v={fmtEuro(r.wk)} color="#DC2626" />
              <Zeile k="− Gebäude-AfA" v={fmtEuro(r.afa)} color="#DC2626" />
              <div className="border-t border-gray-100 pt-1.5 mt-1.5">
                <Zeile k="= Überschuss / Verlust" v={fmtEuro(r.ueberschuss)} bold color={r.ueberschuss >= 0 ? '#111827' : '#DC2626'} />
              </div>
            </div>
          </div>
        );
      })}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">Vorläufige Hochrechnung auf Basis der erfassten Daten — ersetzt keine Steuerberatung. Mieteinnahmen = als „bezahlt“ erfasste Eingänge; AfA = hinterlegter Jahresbetrag. Tatsächliche Anlage V mit dem Steuerberater abstimmen.</p>
      </div>
    </div>
  );
}

function Zeile({ k, v, color, bold }: { k: string; v: string; color?: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-bold text-gray-900' : 'text-gray-600'}>{k}</span>
      <span className={bold ? 'font-bold' : 'font-medium'} style={{ color: color || '#111827' }}>{v}</span>
    </div>
  );
}

// ------------------------------------------------------------
// DOKUMENTE
// ------------------------------------------------------------
function Dokumente({ d }: { d: ReturnType<typeof useImmoData> }) {
  const [form, setForm] = useState<any>({ titel: '', kategorie: 'vertrag', drive_url: '', objekt_id: d.objekte[0]?.id || '' });
  const objName = (id: string) => d.objekte.find(o => o.id === id)?.bezeichnung || '';
  async function speichern() {
    if (!form.titel.trim()) { toast.error('Titel fehlt'); return; }
    const { error } = await supabase.from('immo_dokumente').insert({ ...form, datum: HEUTE });
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success('Dokument verlinkt');
    setForm({ ...form, titel: '', drive_url: '' });
    d.reload();
  }
  return (
    <div className="space-y-4">
      <H title="Dokumente" sub="Verträge, Protokolle, Abrechnungen, Bescheide je Objekt" />
      <DokumentUpload objektId={undefined} onUploaded={d.reload} />
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input label="Titel" value={form.titel} onChange={(v) => setForm({ ...form, titel: v })} />
          <Select label="Kategorie" value={form.kategorie} options={['vertrag','kaufvertrag','darlehensvertrag','grundbuch','steuerbescheid','steuerunterlagen','bauplan','mieterliste','uebergabeprotokoll','nebenkostenabrechnung','versicherung','sonstige']} onChange={(v) => setForm({ ...form, kategorie: v })} />
          <Select label="Objekt" value={form.objekt_id} options={d.objekte.map(o => o.id)} labels={d.objekte.map(o => o.bezeichnung)} onChange={(v) => setForm({ ...form, objekt_id: v })} />
          <Input label="Drive-Link (URL)" value={form.drive_url} onChange={(v) => setForm({ ...form, drive_url: v })} />
        </div>
        <button onClick={speichern} className="text-sm font-semibold text-white px-4 py-2 rounded-lg" style={{ backgroundColor: '#059669' }}>Verlinken</button>
      </div>
      {d.dokumente.length === 0 ? <Leer icon={FileText} text="Keine Dokumente verlinkt" /> : (
        <div className="space-y-2">
          {d.dokumente.map(dok => (
            <div key={dok.id} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-300" />
                <div>
                  <p className="text-sm text-gray-800 font-medium">{dok.titel}</p>
                  <p className="text-xs text-gray-400">{[dok.kategorie, objName(dok.objekt_id)].filter(Boolean).join(' · ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {dok.storage_path && <button onClick={() => oeffneDokument(dok.storage_bucket || 'immo-dokumente', dok.storage_path)} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800">Öffnen</button>}
              {dok.drive_url && <a href={dok.drive_url} target="_blank" rel="noreferrer" className="text-amber-700 hover:text-amber-800"><ExternalLink className="w-4 h-4" /></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Form-Primitive
// ============================================================
function Input({ label, value, onChange, type }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      <input type={type || 'text'} value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400" />
    </label>
  );
}

function Select({ label, value, options, labels, onChange }: { label: string; value: string; options: string[]; labels?: string[]; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-amber-400">
        {options.map((o, i) => <option key={o} value={o}>{labels ? labels[i] : o}</option>)}
      </select>
    </label>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button onClick={onClick}
      className="text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors"
      style={active ? { backgroundColor: '#B45309', color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }}>
      {children}
    </button>
  );
}
